/**
 * Events → Channels ("ערוצי הרשמה") — per-channel signup volume vs
 * actual attendance. The signal-to-action goal here is to surface
 * BROKEN channels (high volume, near-zero attendance) BEFORE any
 * topline summary so the decision-maker sees them first.
 *
 * Composes the live `/v1/contract/channels` envelope with the honesty
 * primitives (`<Freshness/>`, `<Denominator/>`, `<AnomalyChip/>`) and
 * the `ChannelLeaderboard` chart primitive.
 */
import type { Suite, ModuleSpec } from '../../../data/types/platform';
import { useEventsChannels } from '../../../data/queries/useEventsChannels';
import { ChannelLeaderboard } from '../../../components/charts/ChannelLeaderboard';
import type { ChannelLeaderboardRow } from '../../../components/charts/ChannelLeaderboard';
import { AnomalyChip, Denominator, Freshness } from '../../../components/honesty';
import type { AnomalySeverity } from '../../../components/honesty/AnomalyChip';
import { Icon } from '../../../components/ui/Icon';
import { formatNumber } from '../../../utils/format';
import type { Channel, Anomaly } from '@biyahad/contracts';

export interface ChannelsViewProps {
  system?: Suite;
  module?: ModuleSpec;
}

/** Volume floor for the "best by conversion" KPI — anything smaller
 *  is statistical noise (single-digit-rate channels with 3 signups). */
const BEST_BY_RATE_MIN_VOLUME = 50;

function toEpoch(iso: string): number {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : Date.now();
}

function severityFromAnomaly(s: Anomaly['severity']): AnomalySeverity {
  return s === 'critical' ? 'alert' : s;
}

/**
 * Client-side Hebrew labels for the most common signup-channel ids.
 * The contract API returns the source label verbatim in `he` even when
 * it's English — these are visual fallbacks so the dashboard reads as
 * Hebrew end-to-end. Anything not in the map keeps the source label
 * (still wrapped in `<bdi>` at render time for clean bidi).
 */
const CHANNEL_HE_LABELS: Record<string, string> = {
  'organic promotion': 'קידום אורגני',
  'paid promotion': 'קידום ממומן',
  'SMS promotion': 'קמפיין SMS',
  'SMS promotion - Yesh Atid': 'קמפיין SMS · יש עתיד',
  'SMS poll promotion': 'קמפיין SMS · סקרים',
  'Whatsapp promotion': 'קמפיין WhatsApp',
  'WA promotion - Yesh Atid': 'קמפיין WhatsApp · יש עתיד',
  'email promotion': 'קמפיין דוא״ל',
  'whatsapp groups': 'קבוצות WhatsApp',
  'tagged link': 'קישור מתויג',
  'events page': 'דף האירועים',
  'call center': 'מוקד טלפוני',
  'phone call': 'שיחה אישית',
  'Yesh Atid': 'יש עתיד',
  'Yesh Atid phone banking': 'יש עתיד · phone banking',
  'Yesh Atid Phone Banking': 'יש עתיד · phone banking',
  'bennett social': 'רשתות בנט',
  'QR code': 'קוד QR',
  'walk-in': 'ללא הרשמה מוקדמת',
};

function channelLabelHe(c: Channel): string {
  const byId = CHANNEL_HE_LABELS[c.id];
  if (byId) return byId;
  const byHe = CHANNEL_HE_LABELS[c.he];
  if (byHe) return byHe;
  return c.he;
}

export function ChannelsView({ system, module }: ChannelsViewProps) {
  const { data, isLoading, error } = useEventsChannels();

  return (
    <article className="module-view channels-view module-page">
      <header className="mp-head">
        <div className="mp-eyebrow">
          <span>{system?.en ?? 'EVENTS'}</span>
          <span className="mp-sep">/</span>
          <span>{module?.he ?? 'ערוצי הרשמה'}</span>
        </div>
        <h1>{module?.he ?? 'ערוצי הרשמה'}</h1>
        <p className="mp-sub">
          {module?.sub ??
            'נפח מול איכות — מי גורר רישומים שמתרגמים להגעה בפועל, ומי לא.'}
        </p>
        <div className="mp-meta">
          {data && (
            <>
              <Freshness updatedAt={toEpoch(data.source_pulled_at)} />
              <span className="mp-dot" />
              <span>scope: {data.scope}</span>
              {data.meta.mock && (
                <>
                  <span className="mp-dot" />
                  <span>mock fixtures</span>
                </>
              )}
            </>
          )}
        </div>
      </header>

      <div className="mp-body">
        {isLoading && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="loader" size={20} />
                <span>טוען נתוני ערוצים…</span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="alert-triangle" size={20} />
                <span>שגיאה בטעינת המודול: {error.message}</span>
              </div>
            </div>
          </div>
        )}

        {data && <ChannelsBody envelope={data} />}
      </div>
    </article>
  );
}

interface ChannelsBodyProps {
  envelope: NonNullable<ReturnType<typeof useEventsChannels>['data']>;
}

function ChannelsBody({ envelope }: ChannelsBodyProps) {
  const channels: Channel[] = envelope.data;
  // Per-channel anomalies surfaced by the transform layer. Any
  // anomaly whose `key` starts with "channel:" is one of ours; the
  // brief specifies these MUST render at the top before KPIs.
  const channelAnomalies = envelope.meta.anomalies.filter((a) =>
    a.key.startsWith('channel:'),
  );

  // Build leaderboard rows. `attended` is derived from
  // `total_signups * rate_overall` since the contract does not store
  // it explicitly. We attach the matching anomaly chip via the row id.
  const anomalyByLabel = new Map<string, Anomaly>();
  for (const a of channelAnomalies) {
    // Anomaly key shape: "channel:<exact-channel-id>".
    const id = a.key.slice('channel:'.length);
    anomalyByLabel.set(id, a);
  }

  const rows: ChannelLeaderboardRow[] = channels.map((c) => {
    const attended = Math.round(c.total_signups * c.rate_overall);
    const heLabel = channelLabelHe(c);
    const anomaly =
      anomalyByLabel.get(c.id) ??
      (c.anomaly
        ? ({
            key: `channel:${c.id}`,
            severity: 'warn',
            scope: envelope.scope,
            metric: 'channel.attendance_rate',
            observed: c.rate_overall,
            threshold: 0.05,
            message: `${heLabel} — ${formatNumber(c.total_signups)} הרשמות, ${Math.round(c.rate_overall * 100)}% הגעה.`,
          } as Anomaly)
        : undefined);
    return {
      id: c.id,
      label: heLabel,
      total_signups: c.total_signups,
      attended,
      rate: c.rate_overall,
      anomaly: anomaly
        ? { severity: severityFromAnomaly(anomaly.severity), reason: anomaly.message }
        : undefined,
    };
  });

  // KPI calculations.
  const totalChannels = channels.length;
  const totalSignups = channels.reduce((a, c) => a + c.total_signups, 0);

  // Best by conversion, with a volume floor so a 3-signup channel at
  // 100% doesn't win.
  const eligibleForBest = channels.filter(
    (c) => c.total_signups >= BEST_BY_RATE_MIN_VOLUME && !c.anomaly && !c.note,
  );
  const bestByRate = eligibleForBest.reduce<Channel | null>((best, c) => {
    if (!best) return c;
    return c.rate_overall > best.rate_overall ? c : best;
  }, null);

  // Biggest by volume — exclude the walk-in pseudo-channel (it is
  // "attended only" and doesn't represent an acquisition channel).
  const bigByVolume = channels
    .filter((c) => !c.note)
    .reduce<Channel | null>((best, c) => {
      if (!best) return c;
      return c.total_signups > best.total_signups ? c : best;
    }, null);

  const brokenCount = rows.filter((r) => r.anomaly).length;

  return (
    <>
      {/* Anomaly callout — TOP of the page, before KPIs. The brief is
          explicit: broken channels must be the first thing the user
          sees. */}
      {channelAnomalies.length > 0 && (
        <div className="notice warn" style={{ marginBottom: 16 }}>
          <Icon name="alert-triangle" size={18} />
          <div>
            <b>
              {channelAnomalies.length === 1
                ? 'זוהה ערוץ פגום'
                : `זוהו ${channelAnomalies.length} ערוצים פגומים`}
              .
            </b>{' '}
            נפח הרשמות גבוה אבל אחוז הגעה אפסי — בחן את איכות הרשימה מול
            הספק לפני הקצאת תקציב נוסף.
            <ul
              style={{
                margin: '8px 0 0',
                padding: 0,
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              {channelAnomalies.map((a) => (
                <li key={a.key} style={{ fontSize: 12 }}>
                  • <bdi>{a.message}</bdi>
                </li>
              ))}
            </ul>
            <div
              style={{
                fontSize: 11.5,
                marginTop: 8,
                paddingTop: 8,
                borderTop: '1px solid rgba(0,0,0,0.1)',
                lineHeight: 1.5,
              }}
            >
              <b>שים לב:</b> ייתכן שאלה רישומים פגומים בקובץ המקור (לדוגמה רשימה
              שיובאה ולא סומנה כניסה). מומלץ לבדוק ב-Airtable את הרשומות בערוצים
              אלו לפני הסקת מסקנות עסקיות.
            </div>
          </div>
        </div>
      )}

      {/* KPI strip — totals, best, biggest, broken. */}
      <div className="kpi-grid cols-4">
        <KpiCell
          label="סה״כ ערוצים"
          value={formatNumber(totalChannels)}
          sub={`${formatNumber(totalSignups)} הרשמות בסך-הכל`}
        />
        <KpiCell
          label="הערוץ הטוב ביותר"
          value={bestByRate ? channelLabelHe(bestByRate) : '—'}
          sub={
            bestByRate
              ? `${Math.round(bestByRate.rate_overall * 100)}% הגעה · ${formatNumber(bestByRate.total_signups)} הרשמות`
              : `אין ערוץ עם נפח ≥ ${BEST_BY_RATE_MIN_VOLUME}`
          }
          accent
        />
        <KpiCell
          label="הערוץ הגדול ביותר"
          value={bigByVolume ? channelLabelHe(bigByVolume) : '—'}
          sub={
            bigByVolume
              ? `${formatNumber(bigByVolume.total_signups)} הרשמות · ${Math.round(bigByVolume.rate_overall * 100)}% הגעה`
              : '—'
          }
        />
        <KpiCell
          label="ערוצים פגומים"
          value={formatNumber(brokenCount)}
          sub={brokenCount > 0 ? 'נפח גבוה · הגעה אפסית' : 'אין חריגות פעילות'}
          focus={brokenCount > 0}
        />
      </div>

      {/* Main panel — leaderboard. */}
      <div className="panel">
        <div className="panel-head">
          <div className="title">דירוג ערוצים</div>
          <div className="sub">
            ממוין לפי נפח הרשמות. שורות בצבע ענבר — חריגות שאותרו במנגנון
            בקרת האיכות.
          </div>
        </div>
        <ChannelLeaderboard rows={rows} />
      </div>

      {/* Honesty footer: denominators + framing copy + anomaly chips. */}
      <footer
        className="mv-foot"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          padding: '16px 0 4px',
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          color: 'var(--ink-3)',
          alignItems: 'center',
        }}
      >
        <Denominator
          n={totalSignups}
          total={totalSignups}
          label="הרשמות מוצלבות לערוץ"
        />
        <span>
          "הטוב ביותר" מחושב מערוצים עם ≥ {BEST_BY_RATE_MIN_VOLUME} הרשמות
          כדי להימנע מרעש סטטיסטי.
        </span>
        {channelAnomalies.map((a) => (
          <AnomalyChip
            key={a.key}
            severity={severityFromAnomaly(a.severity)}
            reason={a.message}
          />
        ))}
      </footer>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Local KpiCell — mirrors the funnel-view helper.                    */
/* ------------------------------------------------------------------ */

interface KpiCellProps {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  focus?: boolean;
}

function KpiCell({ label, value, sub, accent, focus }: KpiCellProps) {
  const cls = ['kpi-cell', accent && 'accent', focus && 'focus']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-meta">{sub}</div>}
    </div>
  );
}
